import i18n from 'i18next';
import { t } from 'i18next';
import { CurrencyCode, currencyLocaleMap } from '../schemes/items';

interface Currency {
    id: number;
    name_en: string;
    name_uz: string;
    name_ru: string;
    code: string;
    rate: number;
    name_tr: string;
}

const getLocale = (): string => {
    // fallback to en-US if your i18n lang is garbage
    const lang = i18n.language || 'en';
    const map: Record<string, string> = {
        en: 'en-US',
        uz: 'uz-UZ',
        ru: 'ru-RU',
        tr: 'tr-TR'
    };
    return map[lang] ?? 'en-US';
};

export const formatCurrency = (val: number, currency?: string | undefined, currencyId?: number | undefined) => {
    if (!currency && currencyId) {
        currency = getCurrencyCodeById(currencyId);
    }
    try {
        return new Intl.NumberFormat(getLocale(), {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol' // could also use 'narrowSymbol' or 'name'
        }).format(val);
    } catch {
        // because you’ll inevitably pass garbage codes here
        return `${val.toFixed(2)} ${currency}`;
    }
};
const getCurrencyName = (currency: CurrencyCode): string => {
    const lang = (i18n.language || 'en') as 'en' | 'uz' | 'ru' | 'tr';
    const currencyEntry = currencyData.find((c) => c.code === currency);
    if (!currencyEntry) return currency;

    const nameKey = `name_${lang}` as keyof typeof currencyEntry;
    return (currencyEntry[nameKey] as string) || currency;
};

export const formatPrice = (amount: number, currency: CurrencyCode) => {
    if (amount === 0) return t('item_view.free');
    if (amount === -1) return '';

    const locale = currencyLocaleMap[currency] ?? getLocale();
    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0
    }).format(amount);

    return `${formatted} ${getCurrencyName(currency)}`;
};

export const getCurrencyDisplayName = (currency: CurrencyCode): string => {
    try {
        const dn = new Intl.DisplayNames([getLocale()], { type: 'currency' });
        return dn.of(currency) ?? currency;
    } catch {
        return currency;
    }
};
export const currencyData: Currency[] = [
    {
        id: 1,
        name_en: 'SUM',
        name_uz: "SO'M",
        name_ru: 'СУМ',
        code: 'UZS',
        rate: 1,
        name_tr: 'SUM'
    },
    {
        id: 2,
        name_en: 'RUBLE',
        name_uz: 'RUBL',
        name_ru: 'РУБЛЕЙ',
        code: 'RUB',
        rate: 1,
        name_tr: 'RUBLE'
    },
    {
        id: 3,
        name_en: 'USD',
        name_uz: 'USD',
        name_ru: 'USD',
        code: 'USD',
        rate: 1,
        name_tr: 'USD'
    },
    {
        id: 4,
        name_en: 'EUR',
        name_uz: 'YEVRO',
        name_ru: 'ЕВРО',
        code: 'EUR',
        rate: 1,
        name_tr: 'EUR'
    },
    {
        id: 5,
        name_en: 'RM',
        name_uz: 'RM',
        name_ru: 'RM',
        code: 'MYR',
        rate: 1,
        name_tr: 'RM'
    },
    {
        id: 7,
        name_en: 'TRY',
        name_uz: 'LIRA',
        name_ru: 'ЛИРА',
        code: 'TRY',
        rate: 1,
        name_tr: 'LIRA'
    }
];

export const getCurrencyCodeById = (id: number): string => currencyData.find((c) => c.id === id)?.code ?? 'UZS';

const CLICK_SERVICE_ID = 29208;
const CLICK_MERCHANT_ID = 21511;
const PAYME_MERCHANT_ID = '6893399ccab302211ad209f1';
//const RETURN_URL = 'https://dev.uzoffer.com/paymentStatus';

export const PAYMENT_URLS = {
    Click: (amount: number, orderId: string | number) => {
        const amountInt = Math.round(Number(amount));
        const params = new URLSearchParams({
            service_id: CLICK_SERVICE_ID.toString(),
            merchant_id: CLICK_MERCHANT_ID.toString(),
            amount: String(amountInt),
            transaction_param: String(orderId),
            lang: i18n.language
        });
        return `https://my.click.uz/services/pay?${params.toString()}`;
    },

    // amount: in UZS (will be converted to tiyin for Payme; 1 UZS = 100 tiyin)
    Payme: (amount: number, orderId: string | number) => {
        const amountTiyin = Math.round(Number(amount) * 100);
        const raw = `m=${PAYME_MERCHANT_ID};ac.order_id=${orderId};a=${amountTiyin};l=${i18n.language}`;
        return `https://checkout.paycom.uz/${btoa(raw)}`;
    }
};
